import type { ProductContextId } from '@vs-shop/schemas/id/ProductsContextId';
import type { AttributeCode } from '@vs-shop/schemas/product/filter/state/attributeCode';
import type {
  RangeFilterStateMap,
  RangeFilterValue,
} from '@vs-shop/schemas/product/filter/state/range';
import { Context, Duration, Effect, HashMap, Layer, Option, Stream, SubscriptionRef } from 'effect';

import { ContextTag } from '../context';

type ContextualRangeFilterStateMap = HashMap.HashMap<ProductContextId, RangeFilterStateMap>;

// ===== EXPORTED UTILITIES =====
// Standalone stream utilities for atom usage

export const createRangeFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualRangeFilterStateMap>,
  context: ProductContextId
) => {
  const emptyFilterMap = HashMap.empty<AttributeCode, RangeFilterValue>();
  return SubscriptionRef.changes(ref).pipe(
    Stream.changes,
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(Option.getOrElse(() => emptyFilterMap)),
    Stream.changes,
    Stream.debounce(Duration.millis(300))
  );
};

export const createRangeFilterByCodeStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualRangeFilterStateMap>,
  context: ProductContextId,
  attributeCode: AttributeCode
) =>
  SubscriptionRef.changes(ref).pipe(
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(Option.flatMap((filters) => HashMap.get(filters, attributeCode)))
  );

export const createRangeHasActiveFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualRangeFilterStateMap>,
  context: ProductContextId
) =>
  createRangeFiltersStream(ref, context).pipe(Stream.map((filters) => HashMap.size(filters) > 0));

export class RangeFilterService extends Context.Service<RangeFilterService>()(
  ContextTag.RangeFilterService,
  {
    make: Effect.gen(function* () {
      const emptyContextMap = HashMap.empty<ProductContextId, RangeFilterStateMap>();
      const emptyFilterMap = HashMap.empty<AttributeCode, RangeFilterValue>();

      const filtersRef =
        yield* SubscriptionRef.make<ContextualRangeFilterStateMap>(emptyContextMap);

      const getFilters = (context: ProductContextId) =>
        Effect.gen(function* () {
          const contextMap = yield* SubscriptionRef.get(filtersRef);
          return HashMap.get(contextMap, context).pipe(Option.getOrElse(() => emptyFilterMap));
        });

      const getFilterByCode = (context: ProductContextId, attributeCode: AttributeCode) =>
        Effect.gen(function* () {
          const contextFilters = yield* getFilters(context);
          return HashMap.get(contextFilters, attributeCode);
        });

      const setFilter = (
        context: ProductContextId,
        attributeCode: AttributeCode,
        value: RangeFilterValue
      ) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentContextFilters = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyFilterMap)
          );
          const updatedContextFilters = HashMap.set(currentContextFilters, attributeCode, value);
          return HashMap.set(contextMap, context, updatedContextFilters);
        });

      const removeFilter = (context: ProductContextId, attributeCode: AttributeCode) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentContextFilters = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyFilterMap)
          );
          const updatedContextFilters = HashMap.remove(currentContextFilters, attributeCode);

          if (HashMap.isEmpty(updatedContextFilters)) {
            return HashMap.remove(contextMap, context);
          }

          return HashMap.set(contextMap, context, updatedContextFilters);
        });

      const clearFilters = (context: ProductContextId) =>
        SubscriptionRef.update(filtersRef, HashMap.remove(context));

      const setAllFilters = (context: ProductContextId, filters: RangeFilterStateMap) =>
        SubscriptionRef.update(filtersRef, HashMap.set(context, filters));

      // ===== STREAMING METHODS =====
      // Backward compatible wrappers using exported utilities

      const getFiltersStream = (context: ProductContextId) =>
        createRangeFiltersStream(filtersRef, context);

      const getFilterByCodeStream = (context: ProductContextId, attributeCode: AttributeCode) =>
        createRangeFilterByCodeStream(filtersRef, context, attributeCode);

      const hasActiveFiltersStream = (context: ProductContextId) =>
        createRangeHasActiveFiltersStream(filtersRef, context);

      return {
        filtersRef,
        // Effect-based methods (existing)
        getFilters,
        getFilterByCode,
        setFilter,
        removeFilter,
        clearFilters,
        setAllFilters,
        // Stream-based methods (new)
        getFiltersStream,
        getFilterByCodeStream,
        hasActiveFiltersStream,
      };
    }),
  }
) {
  static readonly layer = Layer.effect(this, this.make);
}
